/**
 * App-wide UI state: tab routing (hash-synced), the cinematic narration layer,
 * the guided-tour framework, and a scratch "selectedId" for deep-links
 * (e.g. open a specific client or order from another screen).
 *
 * Data lives in Dexie — this context is purely for view/orchestration state.
 */

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

export type TabKey =
  | 'panou'
  | 'mesaje'
  | 'comenzi'
  | 'clienti'
  | 'catalog'
  | 'flota'
  | 'facturare'
  | 'rapoarte'

export const TAB_KEYS: TabKey[] = [
  'panou',
  'mesaje',
  'comenzi',
  'clienti',
  'catalog',
  'flota',
  'facturare',
  'rapoarte',
]

export interface TabDef {
  key: TabKey
  label: string
  /** short RO description, shown as the screen subtitle */
  descriere: string
}

export const TABS: TabDef[] = [
  { key: 'panou', label: 'Panou', descriere: 'Imaginea zilei dintr-o privire' },
  { key: 'mesaje', label: 'Mesaje', descriere: 'De la WhatsApp la comandă, în câteva secunde' },
  { key: 'comenzi', label: 'Comenzi', descriere: 'Fluxul comenzilor, de la primire la facturare' },
  { key: 'clienti', label: 'Clienți', descriere: 'Toți clienții și istoricul lor, la un loc' },
  { key: 'catalog', label: 'Catalog', descriere: 'Produse, prețuri și stocuri' },
  { key: 'flota', label: 'Flotă & Șoferi', descriere: 'Mașini, șoferi și trasee de livrare' },
  { key: 'facturare', label: 'Facturare', descriere: 'Facturi, TVA și e-Factura ANAF' },
  { key: 'rapoarte', label: 'Rapoarte', descriere: 'Vânzări pe produs, client și zi' },
]

/** A single guided-tour step (populated by the tour/cinematic agent). */
export interface TourStep {
  tab: TabKey
  /** `data-tour="<anchor>"` id to anchor the tooltip to (optional; centers if absent) */
  anchor?: string
  titlu: string
  text: string
}

/** Minimal fallback tour so "Tur ghidat" works before the tour agent lands. */
const BUILTIN_TOUR: TourStep[] = [
  {
    tab: 'panou',
    titlu: 'Panoul de control',
    text: 'Toată ziua dintr-o privire: comenzi, kilograme livrate, încasări și mașini pe traseu.',
  },
  {
    tab: 'mesaje',
    titlu: 'De la mesaj la comandă',
    text: 'Mesajul de pe WhatsApp devine comandă cu prețuri, fără să mai scrii nimic de mână.',
  },
  {
    tab: 'comenzi',
    titlu: 'Fluxul comenzilor',
    text: 'Fiecare comandă trece prin pași clari, de la „Nou" până la „Facturat".',
  },
  {
    tab: 'facturare',
    titlu: 'Facturi și e-Factura',
    text: 'Factura se generează dintr-o livrare și pleacă la ANAF — cu TVA 9% și cod UIT pentru carne.',
  },
]

interface AppState {
  activeTab: TabKey
  setActiveTab: (tab: TabKey) => void

  /** deep-link scratch: which entity a screen should focus/open */
  selectedId: string | null
  setSelectedId: (id: string | null) => void
  /** navigate to a tab and focus an entity in one call */
  openOn: (tab: TabKey, id?: string | null) => void

  // --- cinematic narration ---
  caption: string | null
  setCaption: (c: string | null) => void
  demoRunning: boolean
  setDemoRunning: (b: boolean) => void
  /**
   * Demo-runner registry. The cinematic agent calls `registerDemoRunner(fn)`
   * from a mounted component; the Sidebar's "Simulează o comandă" calls
   * `runDemo()`. Keeps the cinematic decoupled from the foundation shell.
   */
  registerDemoRunner: (fn: (() => void) | null) => void
  runDemo: () => void

  // --- guided tour framework ---
  tourActive: boolean
  setTourActive: (b: boolean) => void
  tourSteps: TourStep[]
  setTourSteps: (s: TourStep[]) => void
  tourIndex: number
  setTourIndex: (i: number) => void
  startTour: (steps?: TourStep[]) => void
  nextTour: () => void
  prevTour: () => void
  stopTour: () => void
}

const AppContext = createContext<AppState | null>(null)

function tabFromHash(): TabKey {
  const h = window.location.hash.replace('#', '') as TabKey
  return TAB_KEYS.includes(h) ? h : 'panou'
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [activeTab, setActiveTabState] = useState<TabKey>(() =>
    typeof window !== 'undefined' ? tabFromHash() : 'panou',
  )
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const [caption, setCaption] = useState<string | null>(null)
  const [demoRunning, setDemoRunning] = useState(false)
  const [demoRunner, setDemoRunner] = useState<(() => void) | null>(null)

  const [tourActive, setTourActive] = useState(false)
  const [tourSteps, setTourSteps] = useState<TourStep[]>([])
  const [tourIndex, setTourIndex] = useState(0)

  const setActiveTab = useCallback((tab: TabKey) => {
    setActiveTabState(tab)
    if (typeof window !== 'undefined' && tabFromHash() !== tab) {
      window.location.hash = tab
    }
  }, [])

  const openOn = useCallback(
    (tab: TabKey, id: string | null = null) => {
      setSelectedId(id)
      setActiveTab(tab)
    },
    [setActiveTab],
  )

  // keep tab in sync with browser back/forward
  useEffect(() => {
    const onHash = () => setActiveTabState(tabFromHash())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  const registerDemoRunner = useCallback((fn: (() => void) | null) => {
    setDemoRunner(() => fn)
  }, [])

  const runDemo = useCallback(() => {
    if (demoRunner) {
      demoRunner()
      return
    }
    // graceful fallback before the cinematic agent registers a runner
    setActiveTab('mesaje')
    setCaption('Pornește „Simulează o comandă" din bara laterală.')
  }, [demoRunner, setActiveTab])

  const startTour = useCallback(
    (steps?: TourStep[]) => {
      const next = steps ?? (tourSteps.length ? tourSteps : BUILTIN_TOUR)
      setTourSteps(next)
      setTourIndex(0)
      setTourActive(true)
    },
    [tourSteps],
  )

  const stopTour = useCallback(() => {
    setTourActive(false)
    setTourIndex(0)
  }, [])

  const nextTour = useCallback(
    () => setTourIndex((i) => Math.min(i + 1, Math.max(0, tourSteps.length - 1))),
    [tourSteps.length],
  )
  const prevTour = useCallback(() => setTourIndex((i) => Math.max(i - 1, 0)), [])

  const value = useMemo<AppState>(
    () => ({
      activeTab,
      setActiveTab,
      selectedId,
      setSelectedId,
      openOn,
      caption,
      setCaption,
      demoRunning,
      setDemoRunning,
      registerDemoRunner,
      runDemo,
      tourActive,
      setTourActive,
      tourSteps,
      setTourSteps,
      tourIndex,
      setTourIndex,
      startTour,
      nextTour,
      prevTour,
      stopTour,
    }),
    [
      activeTab,
      setActiveTab,
      selectedId,
      openOn,
      caption,
      demoRunning,
      registerDemoRunner,
      runDemo,
      tourActive,
      tourSteps,
      tourIndex,
      startTour,
      nextTour,
      prevTour,
      stopTour,
    ],
  )

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export function useApp(): AppState {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useApp trebuie folosit în interiorul <AppProvider>')
  return ctx
}
